import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { mountRootParcel, Parcel } from 'single-spa';
import { Button, Layout, Typography } from '@robinpowered/ui-kit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPenToSquare,
  faXmark,
} from '@awesome.me/kit-cf858715fd/icons/classic/solid';
import {
  AuthBridgeRef,
  parseEventParam,
  useAgentPromptRequests,
  useEmitSiderOccupancy,
  useEmitSiderRequestForeground,
  useFeatureFlags,
  useSiderForegroundDenied,
  useSiderOccupancy,
  useSiderRequestForeground,
} from '@robinpowered/dashboard-apps-common';
import { useTranslation } from 'react-i18next';
import { Sentry } from 'lib/sentry';
import { config } from 'config';
import {
  SHELL_AGENT_SIDER_ID,
  closeAgentChat,
  isAgentChatOpen,
  openAgentChat,
} from './agentChatParam';
import { parseProfileSidebarParams } from 'utils/profileSidebarParams';
import { useRestoreAgentChatOnNavigation } from './useRestoreAgentChatOnNavigation';
import { AgentChatBadge } from './AgentChatBadge';
import {
  AgentPromptBridge,
  AgentPromptBridgeRef,
  createAgentPromptBridge,
} from 'lib/agentPromptBridge';
import {
  AgentShellEvent,
  buildViewDeskPath,
  buildViewSpacePath,
} from './agentShellEvents';

const { Sider } = Layout;

// Matches the shell composer / office details sider width so slot handoffs
// between right-hand siders land on the same rectangle (see EventOverlayHost).
const SIDER_WIDTH = 340;

type AgentChatSidebarProps = {
  authBridge: AuthBridgeRef;
};

/**
 * The app's single-spa bundle (`<config.agentChatUrl>/agent-chat-app.js`) exports
 * bootstrap/mount/unmount lifecycles and reads auth from the `authBridge` customProp
 *
 * Visibility is driven by the canonical `agent` query param (SHELL_AGENT_PARAM).
 * The Sider is an always-mounted sibling of the MFE mount container, so
 * opening/closing never mutates the pathname and the underlying MFE is unaffected.
 * The param persists across app transitions — navigations that change the
 * pathname without carrying it forward get it restored in place (see
 * useRestoreAgentChatOnNavigation) — so the panel stays open until closed.
 *
 * Participates in the "one sider" policy (common/common/src/types/siderChannel.ts).
 * It yields the slot to the shell event composer whenever the `event` param is present
 * (staying mounted but collapsed so the in-memory conversation survives).
 */
export const AgentChatSidebar = ({
  authBridge,
}: AgentChatSidebarProps): JSX.Element | null => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('globalNavigation');
  const { checkFlag, loading: flagsLoading } = useFeatureFlags();

  const enabled =
    !!config.agentChatUrl && checkFlag('hydra(dashboard-agent)', false);

  // Strip a stale `agent` param while the feature is off (flag disabled
  // mid-session, or a bookmarked/shared URL in an environment without the
  // feature). MFE siders suspend themselves on the param alone, and every
  // other close path here is gated on `enabled` — without this, the stale
  // param would keep those siders hidden/collapsed with nothing rendering.
  // Waits for flags to load: checkFlag returns its default (false) during the
  // initial fetch, and stripping then would close a deep-linked open panel.
  useEffect(() => {
    if (!enabled && !flagsLoading && isAgentChatOpen(searchParams.toString())) {
      closeAgentChat();
    }
  }, [enabled, flagsLoading, searchParams]);

  // Set when a foreign sider's request closes this panel: an MFE open-click
  // often yields and navigates in the SAME commit (schedule's announcements /
  // day-sidebar buttons), and without the mark the restore hook would read the
  // batched "pathname changed + param gone" as an app transition and reopen
  // the panel the yield just closed.
  const explicitCloseRef = useRef(false);

  // Keep the panel open across app transitions: nav links and MFE-built URLs
  // assemble their destination from scratch, so a pathname change drops our
  // param and would silently close the panel. Re-applying the param never
  // evicts a composer the destination opened — direction 2 (asking the
  // composer to yield) happens only on the user's Assistant click, in
  // useToggleAgentChat — so a restored panel just backgrounds under it.
  const restorePending = useRestoreAgentChatOnNavigation({
    enabled,
    onRestore: openAgentChat,
    explicitCloseRef,
  });

  // A pending restore counts as open: the param was transiently dropped by the
  // navigation and is about to be re-applied, and rendering that dip would
  // flicker the panel closed and back open on every app transition.
  const isOpen =
    enabled && (isAgentChatOpen(searchParams.toString()) || restorePending);
  const isComposerOpen = useMemo(
    () => parseEventParam(searchParams.get('event')).mode !== null,
    [searchParams]
  );
  // Direction 1: The composer opening over us wins.
  // We collapse (but stay mounted, preserving the chat) until it closes.
  const visible = isOpen && !isComposerOpen;

  // Mount the parcel on first open and keep it mounted across collapses — the
  // chat app holds its conversation in memory, so unmounting would discard it.
  const [hasOpened, setHasOpened] = useState(false);
  useEffect(() => {
    if (visible) {
      setHasOpened(true);
    }
  }, [visible]);

  // Direction 2's deny path: the user backed out of the composer's discard
  // dialog after the Assistant click asked it to yield (useToggleAgentChat).
  // Drop our param so the pending open doesn't linger for the next time the
  // composer happens to close.
  useSiderForegroundDenied(
    useCallback(() => {
      if (isOpen && isComposerOpen) {
        closeAgentChat();
      }
    }, [isOpen, isComposerOpen])
  );

  // Direction 2, other side: an MFE sider (office details, etc.) asked for
  // the slot. Unlike the composer we hold no unsaved state, so yield
  // unconditionally by dropping our param — the panel stays mounted, so the
  // conversation survives a later reopen. This also covers the request being
  // aimed at the composer while it covers us (office desk click during
  // compose): if we kept our param, the composer's close would just resurface
  // this panel over the sider the user asked for. Our own requests (the open
  // transition above) carry our id and are skipped.
  useSiderRequestForeground(
    useCallback(
      (id) => {
        if (id !== SHELL_AGENT_SIDER_ID && isOpen) {
          // A yield, not a transition loss — the requester's click may
          // navigate in this same commit (see explicitCloseRef above).
          explicitCloseRef.current = true;
          closeAgentChat();
        }
      },
      [isOpen]
    )
  );

  // Whether an MFE right-hand sider occupies the slot, mirrored from
  // EventOverlayHost (see there for the set-of-ids rationale). Presentation
  // only: opening or closing over an occupied slot (office's details sider)
  // swaps instantly — equal-width siders cancel and the map doesn't wobble —
  // while an empty slot (schedule's meeting-rooms grid) gets the standard
  // slide. Our own occupancy reports are skipped: this panel occupying the
  // slot itself says nothing about who it would be handing off with.
  const occupiedIdsRef = useRef<Set<string>>(new Set());
  const [siderOccupied, setSiderOccupied] = useState(false);
  // A sider that docks its own Robin Agent panel (tickets' issue details
  // sider) says so on the same report; the floating badge hides while one is
  // on screen, so the user isn't offered two ways into the same assistant.
  const entryPointIdsRef = useRef<Set<string>>(new Set());
  const [hostEntryPointShown, setHostEntryPointShown] = useState(false);
  useSiderOccupancy((id, occupied, displaysOwnAgentEntryPoint) => {
    if (id === SHELL_AGENT_SIDER_ID) {
      return;
    }
    const ids = occupiedIdsRef.current;
    const entryPoints = entryPointIdsRef.current;
    if (occupied) {
      ids.add(id);
    } else {
      ids.delete(id);
    }
    if (occupied && displaysOwnAgentEntryPoint) {
      entryPoints.add(id);
    } else {
      entryPoints.delete(id);
    }
    setSiderOccupied(ids.size > 0);
    setHostEntryPointShown(entryPoints.size > 0);
  });

  // The people profile sidebar participates in the swap policy too, but it is
  // shell-hosted and URL-driven rather than an occupancy reporter — and the
  // same write that opens this panel closes it (agentChatParam.ts), so by the
  // render that shows this panel open the profile params are already gone.
  // Remember the previous committed location's profile state: opening over
  // (or being closed under) the profile is a handoff, and handoffs swap
  // instantly rather than sliding in beside the other panel's exit.
  const profileOpen = parseProfileSidebarParams(searchParams).userId !== null;
  const prevProfileOpenRef = useRef(profileOpen);
  useEffect(() => {
    prevProfileOpenRef.current = profileOpen;
  });

  // The shell composer participates in the swap policy the same way: it is
  // shell-hosted and URL-driven rather than an occupancy reporter, and its
  // half of a handoff resolves across two commits — the Assistant toggle sets
  // our param while the composer is still open (this panel stays covered),
  // THEN the composer's yield clears `event` and this panel reveals. In that
  // reveal commit `isComposerOpen` is already false, so only the previous
  // committed render still knows the reveal is the composer's exit.
  const prevComposerOpenRef = useRef(isComposerOpen);
  useEffect(() => {
    prevComposerOpenRef.current = isComposerOpen;
  });

  // Animate only when the slot is empty: no MFE sider occupying it and no
  // profile or composer handoff in flight. An occupied slot swaps instantly —
  // the two panels cancel and the page content never moves.
  const animateNow =
    !siderOccupied &&
    !profileOpen &&
    !prevProfileOpenRef.current &&
    !isComposerOpen &&
    !prevComposerOpenRef.current;

  // LATCHED per visibility transition, not read live: opening over an MFE
  // sider evicts it, its occupancy flips false, and that re-render can land
  // BEFORE the browser paints the new width — reading the gate live would
  // strip `transition: none` mid-flight and hand the entire width change to
  // antd's animation (verified in-browser: the snap only survives if the gate
  // holds until paint). So the decision is made from the slot state at the
  // moment `visible` changes and held until the next change. Written during
  // render (the derived-state pattern): recomputing from the same inputs is
  // idempotent, so StrictMode/discarded renders are safe.
  const animateRef = useRef(animateNow);
  const prevVisibleRef = useRef(visible);
  if (prevVisibleRef.current !== visible) {
    prevVisibleRef.current = visible;
    animateRef.current = animateNow;
  }
  const animateSlide = animateRef.current;

  // Report slot occupancy (content-level, per the channel contract: stays
  // true while the composer covers us) so the composer swaps instantly
  // instead of sliding when it takes over the slot. Emitted on change with an
  // unmount-only `false`, so an isOpen flip doesn't emit a transient `false`
  // between the old effect's cleanup and the new run.
  const emitOccupancy = useEmitSiderOccupancy();
  useEffect(() => {
    emitOccupancy(SHELL_AGENT_SIDER_ID, isOpen);
  }, [isOpen, emitOccupancy]);
  useEffect(
    () => () => emitOccupancy(SHELL_AGENT_SIDER_ID, false),
    [emitOccupancy]
  );

  // Events raised by the agent app (see agentShellEvents.ts). The parcel
  // captures customProps once at mount, so the emitter's identity must stay
  // stable for the life of the parcel; the live handler (which closes over
  // the current `navigate`) is read through a ref instead.
  const navigate = useNavigate();
  const handleAgentEventRef = useRef<(event: AgentShellEvent) => void>(
    () => undefined
  );
  useEffect(() => {
    handleAgentEventRef.current = (event) => {
      // The agent bundle deploys independently, so the compile-time type is
      // not a runtime guarantee; never let a malformed event throw back into
      // the emitting bundle's code.
      if (typeof event !== 'object' || event === null) {
        return;
      }
      // Only navigate while the panel is visible: a hidden panel can't
      // have been clicked, and acting on a late event would wipe the
      // composer's `event` param (losing an unsaved draft with no
      // discard prompt) or yank a user who already closed the assistant.
      const orgSlug = authBridge.authData?.currentOrg.slug;
      switch (event.type) {
        case 'viewDesk': {
          if (!visible || !orgSlug || !event.payload?.deskId) {
            return;
          }
          navigate(buildViewDeskPath(orgSlug, event.payload));
          break;
        }
        case 'viewSpace': {
          if (!visible || !orgSlug || !event.payload?.spaceId) {
            return;
          }
          navigate(buildViewSpacePath(orgSlug, event.payload));
          break;
        }
        default:
          // A newer agent bundle may emit event types this shell doesn't
          // know yet; ignoring them is the contract (agentShellEvents.ts).
          break;
      }
    };
  }, [authBridge, navigate, visible]);
  const onAgentEvent = useCallback(
    (event: AgentShellEvent) => handleAgentEventRef.current(event),
    []
  );

  // The sider's root element (antd Sider forwards its ref); the badge docks
  // to this element's live left edge via a ResizeObserver.
  const siderRef = useRef<HTMLDivElement>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  // One bridge for the life of the shell session: the parcel captures
  // customProps at mount, so this must never change identity (same reason
  // AppShell holds the auth and composer bridges in refs).
  const agentPromptBridgeRef = useRef<AgentPromptBridge | null>(null);
  if (!agentPromptBridgeRef.current) {
    agentPromptBridgeRef.current = createAgentPromptBridge();
  }
  const agentPromptBridge = agentPromptBridgeRef.current;

  // Prompts host apps send over the shell bridge (tickets' assistant panel and
  // friends). The parcel only mounts once the panel has been open, so the
  // sider has to come forward before the bridge can deliver; until it does,
  // the request waits in the bridge's pending slot.
  //
  // This is a slot request like the nav button's, so it has to go through the
  // same arbitration useToggleAgentChat documents — a bare openAgentChat()
  // sets the param but leaves the panel covered when the composer holds the
  // slot, and the prompt would sit pending until the composer happened to
  // close. Not the toggle itself, though: that would CLOSE an already-open
  // assistant, and sending a prompt must never do that.
  const requestSiderForeground = useEmitSiderRequestForeground();
  useAgentPromptRequests((request) => {
    if (!enabled) {
      return;
    }
    if (!isOpen) {
      // Skipped when already open: replaceSharedUrl fires a synthetic popstate
      // through every mounted router and clears the profile params as a side
      // effect, none of which a second prompt needs.
      openAgentChat();
    }
    if (isComposerOpen) {
      requestSiderForeground(SHELL_AGENT_SIDER_ID);
    }
    agentPromptBridge.send(request);
  });

  const parcelRef = useRef<Parcel | null>(null);
  // The most recent parcel teardown. Mounts await it so a rapid flag
  // disable/enable (or retry) can't mount a second parcel into the container
  // while the previous one is still unmounting out of it.
  const teardownRef = useRef<Promise<unknown>>(Promise.resolve());
  const [loadFailed, setLoadFailed] = useState(false);
  // Bumped by the header's new-conversation button to force a fresh
  // mount cycle after the current parcel is torn down.
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    // Depend on reloadToken to trigger remount
    void reloadToken;
    if (
      !hasOpened ||
      loadFailed ||
      parcelRef.current ||
      !containerRef.current
    ) {
      return;
    }
    let cancelled = false;
    void teardownRef.current.then(() => {
      if (cancelled || parcelRef.current || !containerRef.current) {
        return;
      }
      const bundleUrl = `${config.agentChatUrl}/agent-chat-app.js`;
      const parcel = mountRootParcel(
        // webpackIgnore keeps this a native dynamic import so the browser
        // fetches the bundle from the configured origin at runtime.
        () => import(/* webpackIgnore: true */ bundleUrl),
        {
          domElement: containerRef.current,
          authBridge,
          onAgentEvent,
          // Reader half only — see AgentPromptBridgeRef.
          agentPromptBridge: {
            subscribe: agentPromptBridge.subscribe,
          } as AgentPromptBridgeRef,
        }
      );
      parcel.mountPromise.catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load the agent chat app', err);
        Sentry.captureException(err);
        // Clear the failed parcel and surface a retry state — otherwise one
        // transient load failure would block the sidebar until a full reload.
        if (parcelRef.current === parcel) {
          parcelRef.current = null;
        }
        setLoadFailed(true);
      });
      parcelRef.current = parcel;
    });
    return () => {
      cancelled = true;
    };
    // authBridge is created once per shell session (AppShell holds it in a
    // ref) and never changes identity, onAgentEvent is a stable wrapper over a
    // ref, and agentPromptBridge is held in a ref here; if any of them ever
    // changes identity, the mounted parcel needs parcel.update({ ... }) rather
    // than a remount.
  }, [
    hasOpened,
    loadFailed,
    reloadToken,
    authBridge,
    onAgentEvent,
    agentPromptBridge,
  ]);

  // Tear down the active parcel, per the single-spa parcel contract.
  // Chained on mountPromise because a parcel can't be unmounted mid-transition:
  // if it's already mounted this unmounts immediately, otherwise as soon as
  // mounting finishes. A rejected mountPromise means there's nothing to unmount.
  const unmountParcel = useCallback(() => {
    const parcel = parcelRef.current;
    parcelRef.current = null;
    // The parcel registered the only subscriber; drop it with the parcel so a
    // bundle that forgets its own cleanup cannot starve the bridge.
    agentPromptBridge.reset();
    if (parcel) {
      teardownRef.current = parcel.mountPromise
        .then(() => parcel.unmount())
        .catch(() => undefined);
    }
  }, [agentPromptBridge]);

  // The feature flag can flip off mid-session: the early return below removes
  // the sider DOM but this component stays mounted, so the unmount-on-teardown
  // cleanup never runs. Tear the parcel down here instead, and reset the open
  // and failure state so a later re-enable goes through a fresh cycle.
  useEffect(() => {
    if (!enabled) {
      unmountParcel();
      setHasOpened(false);
      setLoadFailed(false);
    }
  }, [enabled, unmountParcel]);

  // Unmount the parcel with the sider (AppShell teardown).
  useEffect(() => () => unmountParcel(), [unmountParcel]);

  const retryLoad = useCallback(() => setLoadFailed(false), []);

  // Reload the agent: tear down the current parcel (discarding the in-memory
  // conversation) and let the mount effect bring up a fresh one.
  const reloadAgent = useCallback(() => {
    unmountParcel();
    setLoadFailed(false);
    setReloadToken((token) => token + 1);
  }, [unmountParcel]);

  // A collapsed antd Sider keeps its (clipped) children rendered, focusable,
  // and in the accessibility tree; mark the content inert while hidden so
  // tabbing through the page can't land inside the closed panel. Set via ref
  // because the `inert` JSX prop only exists from React 19.
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    contentRef.current?.toggleAttribute('inert', !visible);
  }, [visible]);

  if (!enabled) {
    return null;
  }

  return (
    <>
      <AgentChatBadge
        siderVisible={visible}
        siderRef={siderRef}
        suppressed={hostEntryPointShown}
      />
      <RightSider
        ref={siderRef}
        collapsed={!visible}
        collapsedWidth={0}
        width={SIDER_WIDTH}
        trigger={null}
        reverseArrow
        // Width animation gate: handing off with an occupied slot (an MFE
        // sider, or the profile sidebar the same write closed) must swap
        // INSTANTLY — animating would drag the content sideways under the
        // other panel's exit; an empty slot gets antd's built-in slide.
        // Inline rather than a styled-component rule because antd's own
        // `transition` lives on a class whose specificity/injection order
        // varies with the cssinjs config (cssVar/hashing) — a class override
        // can tie and lose, but an inline style always wins.
        style={{ transition: animateSlide ? undefined : 'none' }}
      >
        <SiderContent ref={contentRef} aria-hidden={!visible}>
          <PanelHeader>
            <Typography.Title level={4} style={{ margin: 0 }}>
              {t('agentChatTitle', 'Robin Agent')}
            </Typography.Title>
            <HeaderActions>
              <Button
                type="text"
                shape="default"
                size="middle"
                icon={<HeaderActionIcon icon={faPenToSquare} />}
                onClick={reloadAgent}
                aria-label={t('agentChatReload', 'New conversation')}
              />
              <Button
                type="text"
                shape="default"
                size="middle"
                icon={<HeaderActionIcon icon={faXmark} />}
                onClick={closeAgentChat}
                aria-label={t('agentChatClose', 'Close assistant')}
              />
            </HeaderActions>
          </PanelHeader>
          <PanelBody>
            <ParcelMount ref={containerRef} $hidden={loadFailed} />
            {loadFailed && (
              <LoadErrorState>
                <Typography.Text>
                  {t('agentChatLoadError', 'The assistant failed to load.')}
                </Typography.Text>
                <Button onClick={retryLoad}>
                  {t('agentChatRetry', 'Try again')}
                </Button>
              </LoadErrorState>
            )}
          </PanelBody>
        </SiderContent>
      </RightSider>
    </>
  );
};

const RightSider = styled(Sider)`
  background: var(--ant-color-bg-container, #ffffff);
  height: 100%;
  overflow: hidden;
  /* The width-animation gate is an inline style on the element (see the
     render) — a class rule here can tie with antd's own transition rule and
     lose on injection order. */
  /* Pin the content to the full sider width so it doesn't reflow while the
     width animates — the sider clips it as it expands (see EventOverlayHost). */
  .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;
    width: ${SIDER_WIDTH}px;
    transition: none;
  }
`;

const SiderContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ant-margin-xs, 8px);
  min-height: 64px;
  padding: var(--ant-padding, 16px);
  border-bottom: 1px solid var(--ant-color-border-secondary, #f0f0f0);
  flex-shrink: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--ant-margin-xxs, 4px);
  flex-shrink: 0;
`;

/* The design's header glyphs are 16x16; antd buttons default icons to the
   14px base font size, which renders them a step too small and thin. */
const HeaderActionIcon = styled(FontAwesomeIcon)`
  font-size: var(--ant-font-size-lg, 16px);
`;

/* Clip, never scroll: the agent app owns scrolling inside its own transcript.
   An `auto` here would add a second scrollbar around the whole panel once the
   conversation outgrows the viewport. */
const PanelBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const ParcelMount = styled.div<{ $hidden: boolean }>`
  flex: 1;
  min-height: 0;
  display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};
  flex-direction: column;

  /* The parcel renders its app root directly into this container; let it
     fill the slot. */
  > * {
    flex: 1;
    min-height: 0;
  }
`;

const LoadErrorState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px;
  text-align: center;
`;
